'use client'

import { appPaths, getResourceBasePath } from '@config'
import { RESOURCE_TYPE, RESOURCE_TYPE_LABEL_MAPPER } from '@models'
import type { StandardCardData } from '@components/common'
import { DATE_FORMAT, formatCaseStudyDateRange, formatDate } from '@utils/date'
import { useSearch } from '@utils/hooks'
import {
  appendInternalUtmToUrl,
  INTERNAL_UTM_MEDIUM,
  INTERNAL_UTM_SOURCE,
} from '@utils/utm'
import {
  SITE_SEARCH_CATEGORY,
  SITE_SEARCH_CONTENT_CATEGORIES,
  type SiteSearchCategory,
  type SiteSearchContentCategory,
  type SiteSearchHitDocument,
  type SiteSearchResponse,
} from 'src/app/api/typesense-import/search-site/search-site.shared'

export {
  SITE_SEARCH_CATEGORY,
  SITE_SEARCH_CONTENT_CATEGORIES,
  type SiteSearchCategory,
  type SiteSearchContentCategory,
}

export const SEARCH_QUERY_PARAM = 'q'
export const SEARCH_CATEGORY_PARAM = 'category'
export const SEARCH_TABS_ELEMENT_ID = 'search-category-tabs'

export const SEARCH_CARDS_PER_CATEGORY = 6
export const SEARCH_CARDS_LIMITS: Record<SiteSearchContentCategory, number> = {
  [SITE_SEARCH_CATEGORY.Services]: 6,
  [SITE_SEARCH_CATEGORY.Industries]: 6,
  [SITE_SEARCH_CATEGORY.Hire]: 6,
  [SITE_SEARCH_CATEGORY.Blogs]: 4,
  [SITE_SEARCH_CATEGORY.CaseStudies]: 3,
}

export const SITE_SEARCH_TAB = {
  All: 'all',
  Services: 'services',
  Resources: 'resources',
  Industries: 'industries',
  Hire: 'hire',
} as const

export type SiteSearchTab =
  (typeof SITE_SEARCH_TAB)[keyof typeof SITE_SEARCH_TAB]

export const SITE_SEARCH_TAB_LABELS: Record<SiteSearchTab, string> = {
  [SITE_SEARCH_TAB.All]: 'All',
  [SITE_SEARCH_TAB.Services]: 'Services',
  [SITE_SEARCH_TAB.Resources]: 'Resources',
  [SITE_SEARCH_TAB.Industries]: 'Industries',
  [SITE_SEARCH_TAB.Hire]: 'Hire',
}

export const SITE_SEARCH_CONTENT_LABELS: Record<
  SiteSearchContentCategory,
  string
> = {
  [SITE_SEARCH_CATEGORY.Services]: 'Services',
  [SITE_SEARCH_CATEGORY.Industries]: 'Industries',
  [SITE_SEARCH_CATEGORY.Hire]: 'Hire',
  [SITE_SEARCH_CATEGORY.Blogs]: 'Blogs',
  [SITE_SEARCH_CATEGORY.CaseStudies]: 'Case Studies',
}

export const SITE_SEARCH_TABS: SiteSearchTab[] = [
  SITE_SEARCH_TAB.All,
  SITE_SEARCH_TAB.Services,
  SITE_SEARCH_TAB.Resources,
  SITE_SEARCH_TAB.Industries,
  SITE_SEARCH_TAB.Hire,
]

export const SEARCH_SECTION_IDS = {
  [SITE_SEARCH_TAB.All]: 'search-all',
  [SITE_SEARCH_TAB.Services]: 'search-services',
  [SITE_SEARCH_TAB.Resources]: 'search-resources',
  [SITE_SEARCH_TAB.Industries]: 'search-industries',
  [SITE_SEARCH_TAB.Hire]: 'search-hire',
  [SITE_SEARCH_CATEGORY.Blogs]: 'search-blogs',
  [SITE_SEARCH_CATEGORY.CaseStudies]: 'search-case-studies',
} as const

export type SearchSectionMeta = {
  heading: string
  viewAllLabel?: string
  href?: string
  resourceType?: RESOURCE_TYPE
}

export const SEARCH_SECTION_META: Record<
  SiteSearchContentCategory,
  SearchSectionMeta
> = {
  [SITE_SEARCH_CATEGORY.Services]: {
    heading: 'SERVICES',
  },
  [SITE_SEARCH_CATEGORY.Industries]: {
    heading: 'INDUSTRIES',
  },
  [SITE_SEARCH_CATEGORY.Hire]: {
    heading: 'HIRE',
  },
  [SITE_SEARCH_CATEGORY.Blogs]: {
    heading: 'BLOGS',
    viewAllLabel: 'View All Blogs',
    href: appPaths.blogs,
    resourceType: RESOURCE_TYPE.Blogs,
  },
  [SITE_SEARCH_CATEGORY.CaseStudies]: {
    heading: 'CASE STUDIES',
    viewAllLabel: 'View All Case Studies',
    href: appPaths.caseStudies,
    resourceType: RESOURCE_TYPE.CaseStudies,
  },
}

export function parseSiteSearchCategory(
  value: string | null | undefined
): SiteSearchTab {
  if (value === SITE_SEARCH_TAB.Services) return SITE_SEARCH_TAB.Services
  if (value === SITE_SEARCH_TAB.Industries) return SITE_SEARCH_TAB.Industries
  if (value === SITE_SEARCH_TAB.Hire) return SITE_SEARCH_TAB.Hire
  if (
    value === SITE_SEARCH_TAB.Resources ||
    value === SITE_SEARCH_CATEGORY.Blogs ||
    value === SITE_SEARCH_CATEGORY.CaseStudies
  ) {
    return SITE_SEARCH_TAB.Resources
  }
  return SITE_SEARCH_TAB.All
}

export function buildSiteSearchHref({
  q = '',
  category = SITE_SEARCH_TAB.All,
}: {
  q?: string
  category?: SiteSearchTab
} = {}): string {
  const params = new URLSearchParams()
  const trimmed = q.trim()

  if (trimmed) {
    params.set(SEARCH_QUERY_PARAM, trimmed)
  }

  if (category !== SITE_SEARCH_TAB.All) {
    params.set(SEARCH_CATEGORY_PARAM, category)
  }

  const qs = params.toString()
  return qs ? `${appPaths.search}?${qs}` : appPaths.search
}

const CATEGORY_TO_RESOURCE_TYPE: Partial<
  Record<SiteSearchCategory, RESOURCE_TYPE>
> = {
  [SITE_SEARCH_CATEGORY.Blogs]: RESOURCE_TYPE.Blogs,
  [SITE_SEARCH_CATEGORY.CaseStudies]: RESOURCE_TYPE.CaseStudies,
}

export type SiteSearchResultKind = 'resource' | 'page'

export type SiteSearchResultItem = {
  kind: SiteSearchResultKind
  title: string
  description?: string
  href: string
  label: string
  resourceType?: RESOURCE_TYPE
  sitemapCategory?: string
  contentTypeSlug?: string
  card: StandardCardData
  textMatch?: number
}

export type SiteSearchGroupedItems = Record<
  SiteSearchContentCategory,
  SiteSearchResultItem[]
>

export function createEmptySearchGroups(): SiteSearchGroupedItems {
  return {
    [SITE_SEARCH_CATEGORY.Services]: [],
    [SITE_SEARCH_CATEGORY.Industries]: [],
    [SITE_SEARCH_CATEGORY.Hire]: [],
    [SITE_SEARCH_CATEGORY.Blogs]: [],
    [SITE_SEARCH_CATEGORY.CaseStudies]: [],
  }
}

export function countGroupedResults(groups?: SiteSearchGroupedItems): number {
  if (!groups) return 0
  return SITE_SEARCH_CONTENT_CATEGORIES.reduce(
    (total, category) => total + groups[category].length,
    0
  )
}

export function countResourceResults(groups?: SiteSearchGroupedItems): number {
  if (!groups) return 0
  return (
    groups[SITE_SEARCH_CATEGORY.Blogs].length +
    groups[SITE_SEARCH_CATEGORY.CaseStudies].length
  )
}

async function searchSite(
  q: string,
  count: number,
  category: SiteSearchCategory
): Promise<SiteSearchResponse> {
  const res = await fetch('/api/typesense-import/search-site', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ q, count, category }),
  })

  if (!res.ok) {
    throw new Error('Search failed')
  }

  return res.json()
}

function withSearchUtm(href: string, query: string) {
  const trimmed = query.trim()
  if (!trimmed) return href
  return appendInternalUtmToUrl(
    href,
    INTERNAL_UTM_SOURCE.search,
    INTERNAL_UTM_MEDIUM.search(trimmed)
  ) as string
}

function buildSearchCardImage(data: SiteSearchHitDocument) {
  if (!data.imageUrl) return undefined
  return {
    alt: data.imageAltTag || data.title,
    src: data.imageUrl,
  }
}

function buildResourceCardFields(
  data: SiteSearchHitDocument,
  isCaseStudy: boolean
): Pick<StandardCardData, 'blogData' | 'caseStudyDateRange' | 'headerData'> {
  return {
    headerData: {
      tag:
        data.categories?.map((item) => ({
          id: item,
          name: item,
          slug: item,
        })) || [],
      ...(data.logoUrl
        ? {
            imageData: {
              alt: data.logoAltTag || data.title,
              src: data.logoUrl,
            },
          }
        : {}),
    },
    ...(isCaseStudy
      ? {
          caseStudyDateRange: formatCaseStudyDateRange(
            data.caseStudyStartDate || null,
            data.caseStudyEndDate || null
          ),
        }
      : {
          blogData: {
            authorName: data.authorName || '',
            timeRead: data.readingTime || '',
            date: data.createdAt
              ? formatDate(data.createdAt, DATE_FORMAT.Short)
              : '',
            authorImage: data.authorImageUrl
              ? {
                  alt: data.authorImageAltTag || '',
                  src: data.authorImageUrl,
                }
              : undefined,
            authorSlug: data.authorSlug,
          },
        }),
  }
}

function adaptResourceHit({
  data,
  resourceType,
  label,
  textMatch,
  query = '',
}: {
  data: SiteSearchHitDocument
  resourceType: RESOURCE_TYPE
  label: string
  textMatch?: number
  query?: string
}): SiteSearchResultItem {
  const resolvedType =
    (data.resourcesType as RESOURCE_TYPE | undefined) || resourceType
  const href = withSearchUtm(
    `${getResourceBasePath(resolvedType)}/${data.slug}`,
    query
  )
  const isCaseStudy = resolvedType === RESOURCE_TYPE.CaseStudies

  const card: StandardCardData = {
    title: data.title,
    description: data.shortDescription,
    href,
    imageData: buildSearchCardImage(data),
    ...buildResourceCardFields(data, isCaseStudy),
  }

  return {
    kind: 'resource',
    title: data.title,
    description: data.shortDescription,
    href,
    label,
    resourceType: resolvedType,
    card,
    textMatch,
  }
}

function adaptPageHit({
  data,
  label,
  textMatch,
  query = '',
}: {
  data: SiteSearchHitDocument
  label: string
  textMatch?: number
  query?: string
}): SiteSearchResultItem {
  const href = withSearchUtm(
    data.href ||
      (data.slug === 'home' || data.slug === '/' ? '/' : `/${data.slug}`),
    query
  )

  const card: StandardCardData = {
    title: data.title,
    description: data.shortDescription,
    href,
    imageData: buildSearchCardImage(data),
  }

  return {
    kind: 'page',
    title: data.title,
    description: data.shortDescription,
    href,
    label,
    sitemapCategory: data.sitemapCategory,
    contentTypeSlug: data.contentTypeSlug,
    card,
    textMatch,
  }
}

function adaptHitsForCategory(
  category: SiteSearchContentCategory,
  hits: NonNullable<SiteSearchResponse['results'][number]['hits']>,
  query: string
): SiteSearchResultItem[] {
  if (
    category === SITE_SEARCH_CATEGORY.Services ||
    category === SITE_SEARCH_CATEGORY.Industries ||
    category === SITE_SEARCH_CATEGORY.Hire
  ) {
    const label = SITE_SEARCH_CONTENT_LABELS[category]
    return hits
      .map((hit) =>
        adaptPageHit({
          data: hit.document,
          label,
          textMatch: hit.text_match,
          query,
        })
      )
      .sort((a, b) => (b.textMatch ?? 0) - (a.textMatch ?? 0))
  }

  const resourceType = CATEGORY_TO_RESOURCE_TYPE[category]
  if (!resourceType) return []

  const label =
    SITE_SEARCH_CONTENT_LABELS[category] ||
    RESOURCE_TYPE_LABEL_MAPPER[resourceType] ||
    'Resource'

  return hits
    .map((hit) =>
      adaptResourceHit({
        data: hit.document,
        resourceType,
        label,
        textMatch: hit.text_match,
        query,
      })
    )
    .sort((a, b) => (b.textMatch ?? 0) - (a.textMatch ?? 0))
}

function adaptGroupedResponse(
  response: SiteSearchResponse,
  query: string
): SiteSearchGroupedItems {
  const grouped = createEmptySearchGroups()

  SITE_SEARCH_CONTENT_CATEGORIES.forEach((category) => {
    const hits =
      response.groups?.[category]?.hits ??
      (response.category === category
        ? response.results[0]?.hits
        : undefined) ??
      []
    grouped[category] = adaptHitsForCategory(category, hits, query).slice(
      0,
      SEARCH_CARDS_LIMITS[category]
    )
  })

  return grouped
}

type Props = {
  enabled?: boolean
  countPerType?: number
  initialSearchValue?: string
  searchDebounceTime?: number
}

/**
 * Site-wide Typesense search grouped by page content type and resources.
 */
export const useSiteWideSearch = ({
  enabled = true,
  countPerType = SEARCH_CARDS_PER_CATEGORY,
  initialSearchValue = '',
  searchDebounceTime = 600,
}: Props = {}) => {
  return useSearch({
    enabled,
    searchDebounceTime,
    initialSearchValue,
    deps: [countPerType],
    asyncFn: (q) =>
      searchSite(q, countPerType, SITE_SEARCH_CATEGORY.All).then((response) =>
        adaptGroupedResponse(response, q)
      ),
    onSuccess: () => {},
  })
}
